import { useEffect, useState } from "react";

import { apolloClient } from "@/apollo/client";
import {
  resetAuthenticationState,
  setAuthenticationState,
  type AuthenticatedUser,
} from "@/apollo/auth-state";
import { GET_DEFAULT_ACCOUNT, type GetDefaultAccountData } from "@/graphql";
import { analytics } from "@/lib/analytics";
import { env } from "@/lib/env";
import { clearObservabilityUser, setObservabilityUser } from "@/observability";

export function useLinkAuthBootstrap(): { ready: boolean } {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        await refreshLinkAuthSession();
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  return { ready };
}

let sessionRefreshInFlight: Promise<boolean> | undefined;

export function refreshLinkAuthSession(): Promise<boolean> {
  if (sessionRefreshInFlight) return sessionRefreshInFlight;

  const refresh = refreshLinkAuthSessionOnce();
  const trackedRefresh = refresh.finally(() => {
    if (sessionRefreshInFlight === trackedRefresh) {
      sessionRefreshInFlight = undefined;
    }
  });
  sessionRefreshInFlight = trackedRefresh;
  return trackedRefresh;
}

async function refreshLinkAuthSessionOnce(): Promise<boolean> {
  try {
    const response = await fetch(`${env.skedLinkBackendUrl}/auth/session`, {
      credentials: "include",
    });
    if (!response.ok) {
      await handleSignOut();
      return false;
    }
    const session = (await response.json()) as {
      user: AuthenticatedUser;
    };
    await handleSignIn(session.user);
    return true;
  } catch (error) {
    console.error("Authentication session check failed", error);
    await handleSignOut();
    return false;
  }
}

async function handleSignIn(user: AuthenticatedUser): Promise<void> {
  const { email, displayName, emailVerified, uid } = user;
  analytics.identify(uid, { email, displayName });
  await apolloClient.clearStore().catch(() => undefined);
  const { data } = await apolloClient.query<GetDefaultAccountData>({
    query: GET_DEFAULT_ACCOUNT,
    fetchPolicy: "network-only",
  });
  const accounts = (data?.instagrams ?? []).map(
    (instagram) => instagram.username
  );
  const selectedAccount = accounts[0] ?? "";
  setAuthenticationState({
    authenticated: true,
    user,
    accounts,
    selectedAccount,
  });
  setObservabilityUser({
    uid,
    email,
    displayName,
    emailVerified,
    selectedAccount,
    accounts,
  });
}

async function handleSignOut(): Promise<void> {
  resetAuthenticationState();
  analytics.reset();
  clearObservabilityUser();
  await apolloClient.clearStore().catch(() => undefined);
}
